import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Globe, Moon, Sun, LogOut, User, Building2, Bot, ShieldCheck } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/shared/components/ui/dropdown-menu'
import { UserAvatar } from '@/shared/components/ui/user-avatar'
import { useThemeStore } from '@/shared/stores/theme-store'
import { SUPPORTED_LOCALES, type LocaleOption } from '@/shared/lib/i18n/constants'
import { useAssistantStore } from '@/features/assistant/store/assistant-store'
import { TenantSwitcher } from './tenant-switcher'
import { ServicesPanel } from './services-panel'
import { UserAccessDialog } from './user-access-dialog'
import { TenantBrandLogo } from './tenant-brand-logo'
import { useAuth } from '@/shared/lib/auth/auth-context'

const ICON_BUTTON_CLASSES =
  'h-8 w-8 text-topbar-foreground/80 hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-0'

export function TopNav() {
  const { t, i18n } = useTranslation()
  const { user, logout, editProfile, switchTenant, isSwitchingTenant } = useAuth()
  const { isOpen: isAssistantOpen, toggle: toggleAssistant } = useAssistantStore()
  const { setTheme, resolvedTheme } = useThemeStore()
  const isDark = resolvedTheme() === 'dark'
  const [accessDialogOpen, setAccessDialogOpen] = useState(false)

  async function handleLogout() {
    await logout()
  }

  return (
    <header className="sticky top-0 z-50 h-14 px-4 flex items-center bg-topbar">
      {/* Logo — left */}
      <Link
        to="/dashboard"
        className="flex items-center shrink-0 mr-3 text-topbar-foreground"
      >
        <TenantBrandLogo surface="topbar" />
      </Link>

      {/* Services menu button */}
      <ServicesPanel />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Assistant button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-topbar-foreground/80 hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-0 gap-1.5 text-xs"
          onClick={toggleAssistant}
          aria-label={isAssistantOpen ? t('login.closeAssistant') : t('login.openAssistant')}
        >
          <Bot className="h-4 w-4" />
          <span>{isAssistantOpen ? t('login.closeAssistant') : t('login.openAssistant')}</span>
        </Button>

        {/* Language selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className={ICON_BUTTON_CLASSES} aria-label={t('login.selectLanguage')}>
              <Globe className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SUPPORTED_LOCALES.map((locale: LocaleOption) => (
              <DropdownMenuItem
                key={locale.code}
                onClick={() => void i18n.changeLanguage(locale.code)}
                className={i18n.language === locale.code ? 'font-semibold' : ''}
              >
                {locale.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          className={ICON_BUTTON_CLASSES}
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          aria-label={isDark ? t('login.switchToLight') : t('login.switchToDark')}
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* Current tenant indicator */}
        {user && user.tenants.length > 1 && (
          <span className="text-xs text-topbar-muted flex items-center gap-1 mr-1">
            <Building2 className="h-3 w-3" />
            {user.tenants.find((t) => t.id === user.organizationId)?.name ?? ''}
          </span>
        )}

        {/* User avatar */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className={`${ICON_BUTTON_CLASSES} rounded-full`}>
              <UserAvatar
                firstname={user?.firstname ?? ''}
                lastname={user?.lastname ?? ''}
                size={28}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {user?.email ?? ''}
            </div>
            <DropdownMenuSeparator />
            {user && (
              <TenantSwitcher
                user={user}
                switchTenant={switchTenant}
                isSwitchingTenant={isSwitchingTenant}
              />
            )}
            <DropdownMenuItem className="gap-2" onClick={() => void editProfile()}>
              <User className="h-3.5 w-3.5" />
              Edit Profile
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => setAccessDialogOpen(true)}>
              <ShieldCheck className="h-3.5 w-3.5" />
              {t('userAccess.menuItem')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 text-destructive focus:text-destructive"
              onClick={() => void handleLogout()}
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <UserAccessDialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen} />
    </header>
  )
}
